import { supabase } from '../lib/supabase';
import type { ProcessedRow } from '../utils/csvValidation';

export interface StudentImportRequest {
  students: ProcessedRow[];
  company_id: string;
  location_id?: string;
}

export interface StudentImportResult {
  success: boolean;
  message: string;
  imported_count?: number;
  skipped_count?: number;
  failed_count?: number;
  /** When true, the import was queued for background processing */
  queued?: boolean;
  /** Background job ID — use with useBackgroundJob hook for live progress */
  job_id?: string;
  invalid_emails?: Array<{
    row: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    reason: string;
  }>;
  validation_errors?: string[];
  warnings?: string[];
  errors?: string[];
  // Staging records structure varies - dynamic import data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  staging_records?: any[];
}

/**
 * Import student data using the Supabase Edge Function
 * @param request The import request containing student data and company info
 * @returns Promise containing the import result
 */
export const importStudentData = async (request: StudentImportRequest): Promise<StudentImportResult> => {
  try {
    // Get the current session to ensure user is authenticated
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      return {
        success: false,
        message: 'You must be logged in to import student data',
        errors: ['Authentication required']
      };
    }

    // Debug: Log what we're sending
    console.warn('Sending request to edge function:', {
      studentsCount: request.students?.length || 0,
      company_id: request.company_id,
      location_id: request.location_id,
      firstStudent: request.students?.[0] || null
    });
    
    // Call the edge function
    const { data, error } = await supabase.functions.invoke('import-students', {
      body: request,
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      }
    });

    if (error) {
      console.error('Edge function error:', error);
      // Log detailed error for debugging but don't expose to user
      return {
        success: false,
        message: 'Failed to import student data. Please try again or contact support if the issue persists.',
        errors: [] // Don't expose detailed error messages to users
      };
    }

    // Log detailed errors for debugging but don't expose to user
    const result = data as StudentImportResult;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[studentImportService] Edge function response:', {
        success: result.success,
        imported_count: result.imported_count,
        skipped_count: result.skipped_count,
        failed_count: result.failed_count,
        message: result.message,
        errors_count: result.errors?.length || 0,
        validation_errors_count: result.validation_errors?.length || 0,
        warnings_count: result.warnings?.length || 0,
      });
    }
    
    if (result.errors && result.errors.length > 0) {
      console.error('Import batch errors (logged for debugging):', result.errors);
      // Replace detailed errors with generic message for user
      result.errors = [];
      if (result.message && !result.message.includes('contact support')) {
        result.message = result.message + ' Please try again or contact support if the issue persists.';
      } else if (!result.message) {
        result.message = 'Failed to import student data. Please try again or contact support if the issue persists.';
      }
    }
    
    return result;

  } catch (error) {
    console.error('Import service error:', error);
    // Log detailed error for debugging but don't expose to user
    return {
      success: false,
      message: 'Failed to import student data. Please try again or contact support if the issue persists.',
      errors: [] // Don't expose technical error details to users
    };
  }
};

/**
 * Get the user's current company ID for import operations
 */
export const getUserCompanyId = async (): Promise<string | null> => {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      return null;
    }

    // Query user's companies - assuming they have at least one
    const { data: relationships, error } = await supabase
      .from('user_company_relationships')
      .select('company_id, role_id')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !relationships || relationships.length === 0) {
      console.error('No company relationships found for user');
      return null;
    }

    return relationships[0].company_id;

  } catch (error) {
    console.error('Error getting user company ID:', error);
    return null;
  }
};
